<template>
    <div class="flex before:bg-accent/20 p-6 overflow-y-clip section-title-wrapper" :class="wrapperClass">
        <div :class="containerClass">
            <component :is="tag" :class="mergedTitleClass">{{ title }}</component>
            <p v-if="subTitle" :class="mergedSubTitleClass">{{ subTitle }}</p>
        </div>
        <slot v-if="hasButton" name="button">
            <Button class="px-0 pl-2" size="sm" label="more" :to="link" />
        </slot>
    </div>
</template>

<script lang="ts" setup>
    import { twMerge } from "tailwind-merge";
    import { Button, type ButtonVariants } from "~/components/ui/button";

    interface Props {
        tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
        hasButton?: boolean;
        buttonProps?: ButtonVariants | null;
        link?: string;
        class?: string;
        title: string;
        titleClass?: string;
        subTitle?: string | null;
        subTitleClass?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        tag: "h2",
        hasButton: false,
        buttonProps: null,
        link: undefined,
        class: "",
        titleClass: "",
        subTitle: null,
        subTitleClass: "",
    });

    const wrapperClass = computed(() => {
        return props.hasButton
            ? "justify-between items-end section-title-wrapper-has-button"
            : "items-center justify-center";
    });

    const containerClass = computed(() =>
        twMerge("relative before:bg-primary/20 text-3xl lg:text-6xl section-title", props.class)
    );

    const mergedTitleClass = computed(() => twMerge("font-serif font-bold", props.titleClass));

    const mergedSubTitleClass = computed(() =>
        twMerge("before:bg-background text-xs lg:text-sm sub-title", props.subTitleClass)
    );
</script>

<style scoped>
    @reference 'tailwindcss';

    .section-title-wrapper {
        @apply relative;
    }

    .section-title-wrapper-has-button::before {
        transition: all 0.5s cubic-bezier(0.85, 0.09, 0.15, 0.91);
        @apply absolute w-3/4 h-px bottom-0 right-0;
    }

    .section-title-wrapper-has-button:hover::before {
        @apply w-full;
    }

    .section-title {
        @apply relative font-bold cursor-default;
    }

    .section-title::before {
        content: "";
        transition: all 1s cubic-bezier(0.85, 0.09, 0.15, 0.91) 0.05s;
        @apply absolute h-full aspect-square top-full right-0 -translate-y-3/4 translate-x-1/3 scale-95 rotate-z-0 -z-5;
    }

    /* .section-title::after {
        content: attr(data-text);
        @apply top-0 left-0 absolute h-full aspect-square text-white whitespace-nowrap w;
    } */

    .section-title-wrapper:hover .section-title::before {
        @apply rotate-z-120 scale-110;
    }

    .section-title .sub-title {
        @apply relative px-1 py-0.5;
    }

    .section-title .sub-title::before {
        transition: width 0.8s cubic-bezier(0.85, 0.09, 0.15, 0.91);
        @apply top-0 right-0 -z-5 absolute w-full h-full;
    }

    .section-title-wrapper:hover .sub-title::before {
        @apply w-0;
    }
</style>
